import * as React from 'react';
import { DayPicker, ChevronProps, DayButtonProps, DayPickerProps } from 'react-day-picker';
import { isBefore } from 'date-fns/isBefore';
import { isAfter } from 'date-fns/isAfter';
import { addDays } from 'date-fns/addDays';
import { FixMeLater } from '../../utils';
import { usePrevious } from '../../hooks';
import { DATE_PICKER_STATIC_STYLES, RDP_VENDOR_STYLES, useDynamicStyles } from './DatePicker.styles';
import { ResponsiveStyleValue, SpacingValue, DatePickerMode, DateRange, DatePickerDirection } from '../../theming';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from '../../icons';
import { useCulture } from '../../culture';

function CustomChevron(props: ChevronProps) {
    const { orientation } = props;
    if (orientation === 'left') {
        return <ChevronLeftIcon biDirectional={true} size="s" />;
    }
    if (orientation === 'right') {
        return <ChevronRightIcon biDirectional={true} size="s" />;
    }
    if (orientation === 'up') {
        return <ChevronUpIcon size="2xs" />;
    }
    return <ChevronDownIcon size="2xs" />;
}
export interface DatePickerProps extends Omit<Partial<DayPickerProps>, 'onSelect' | 'mode' | 'selected'> {
    /**
     * The selected date range. Use this when the `mode` prop is set to `range`.
     */
    selectedRange?: DateRange;

    /**
     * The selected date. Use this when the `mode` prop is set to `single`.
     */
    selectedDate?: Date;

    /**
     * The earliest day which can be selected.
     * @default New Date()
     */
    minDate?: Date;

    /**
     * Callback fired when the selected date is changed. Use this when the `mode` prop is set to `single`.
     */
    onSelectDate?: (day: Date) => void;

    /**
     * Callback fired when the selected date range is changed. Use this when the `mode` prop is set to `range`.
     */
    onSelectRange?: (range: DateRange | undefined) => void;

    /**
     * The required selection mode.
     * @default single
     */
    mode?: DatePickerMode;

    /**
     * If `true`, the time component of the date will be preserved.
     * @default false
     */
    preserveTime?: boolean;

    /**
     * The latest year to end the month navigation.
     */
    toYear?: number;

    /**
     * If `true`, the component will span the width of its container.
     * @default false
     */
    fullWidth?: boolean;

    /**
     * Sets the horizontal spacing that will applied day cells.
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 1
     */
    dayColumnSpacing?: ResponsiveStyleValue<SpacingValue>;

    /**
     * Sets the vertical spacing that will applied day cells.
     * If a number, the value is in pixels and will be multiplied by the theme spacing unit.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default 1
     */
    dayRowSpacing?: ResponsiveStyleValue<SpacingValue>;

    /**
     * Optionally override react-day-picker's default DayButton component with a custom variant.
     */
    customDayButton?: (props: DayButtonProps) => React.JSX.Element;

    /**
     * The direction in which adjacent months will be displayed.
     * @default row
     */
    direction?: ResponsiveStyleValue<DatePickerDirection>;
}

const TODAY = new Date();

/**
 * DatePicker enables the user to select single or a range of dates via calendar view. The component displays the dates in one month or consecutive
 * months to enable selection of start and end dates. We can also toggle between different months. The input props allow for multiple restrictions on the dates
 * selection possible from the user which allows us to restrict the dates if needed. It supports different views such as single date select, disabled view to visualise the
 * calendar.
 */
export const DatePicker: React.ForwardRefExoticComponent<DatePickerProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function DatePicker(
        props: DatePickerProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element {
        const {
            minDate = TODAY,
            mode = 'single',
            onSelectDate,
            onSelectRange,
            selectedDate,
            selectedRange,
            preserveTime = false,
            toYear = TODAY.getFullYear() + 3,
            fullWidth = false,
            dayColumnSpacing = 1,
            dayRowSpacing = 1,
            customDayButton,
            direction = 'row',
            components,
            ...other
        } = props;

        const { dateFnsLocale } = useCulture();

        const fromDate = mode === 'range' ? selectedRange?.from : selectedDate;
        const toDate = mode === 'range' ? selectedRange?.to : undefined;
        const prevFromDate = usePrevious(fromDate);

        const [hoveredToDate, setHoveredToDate] = React.useState<Date | undefined>(undefined);

        if (!preserveTime) {
            // Reset the time on our date values
            fromDate?.setHours(0, 0, 0, 0);
            toDate?.setHours(0, 0, 0, 0);
        }

        // A useEffect which handles the selected dates being reset.
        React.useEffect(() => {
            if (prevFromDate && !fromDate && !toDate) {
                setHoveredToDate(undefined);
            }
        }, [fromDate, prevFromDate, toDate]);

        const handleSelect = React.useCallback(
            (newValue: Date | DateRange | undefined) => {
                // SINGLE MODE
                if (mode === 'single') {
                    const newDate = new Date(newValue as Date);
                    if (preserveTime && fromDate) {
                        // Preserve the time from the current "from" date.
                        const hours = fromDate?.getHours();
                        const minutes = fromDate?.getMinutes();
                        newDate.setHours(hours);
                        newDate.setMinutes(minutes);
                    }

                    onSelectDate?.(newDate);
                    return;
                }

                if (mode === 'range') {
                    // RANGE MODE

                    const { from, to } = newValue as DateRange;

                    // If the from-date is less than our current from-date then we want to restart the range.
                    if (from && fromDate && isBefore(from, fromDate)) {
                        onSelectRange?.({ from });
                        return;
                    }
                    if (to && toDate) {
                        // If we already have a to-date then we want to restart the range using the new to-date.
                        onSelectRange?.({ from: to });
                        return;
                    }

                    // DayPicker always returns a complete range. When a from-date is selected, it will return a to-date equal to this.
                    // We only want to return the to-date if it is greater than the from-date.
                    onSelectRange?.({ from, to: from && to && isAfter(to, from) ? to : undefined });
                }
            },
            [fromDate, mode, onSelectDate, onSelectRange, preserveTime, toDate]
        );

        const handleDayMouseEnter = React.useCallback(
            (day: Date) => {
                if (mode === 'single' || !fromDate) {
                    return;
                }
                // If we have selected a from-date and hover a date that falls after this, this then becomes our "hovered" to-date.
                day.setHours(0, 0, 0, 0);

                if (isAfter(day, fromDate)) {
                    setHoveredToDate(day);
                }
            },
            [fromDate, mode]
        );

        const handleDayMouseLeave = React.useCallback(
            (_day: Date) => {
                if (mode === 'single') {
                    return;
                }
                // Clear "enteredTo".
                setHoveredToDate(undefined);
            },
            [mode]
        );

        const modifiers = React.useMemo(() => {
            if (mode === 'single' || !hoveredToDate || !fromDate || toDate) {
                return undefined;
            }
            // If we are in range mode and have a hovered-to-date date, then return the days which we need to highlight.
            return {
                hoveredRange: { from: addDays(fromDate, 1), to: hoveredToDate },
                hoveredRangeTo: hoveredToDate,
            };
        }, [hoveredToDate, fromDate, mode, toDate]);

        // DayPicker needs to be convinced that we are using the correct type for the "selected" prop as it is mode-specific.
        const modeSpecificProps = React.useMemo(() => {
            if (mode === 'single') {
                return { mode: 'single' as const, selected: fromDate };
            }
            if (mode === 'range') {
                return { mode: 'range' as const, selected: selectedRange };
            }
            return { mode: 'single' as const, selected: undefined };
        }, [fromDate, mode, selectedRange]);

        // Prepare custom components.
        const customComponents: DayPickerProps['components'] = {
            Chevron: CustomChevron,
        };

        if (customDayButton) {
            customComponents.DayButton = customDayButton;
        }

        const dynamicStyles = useDynamicStyles(dayColumnSpacing, dayRowSpacing, direction);

        return (
            <div
                ref={ref}
                style={dynamicStyles}
                css={[RDP_VENDOR_STYLES, DATE_PICKER_STATIC_STYLES]}
                data-date-picker-root={true}
                data-full-width={fullWidth || undefined}
            >
                <DayPicker
                    {...modeSpecificProps}
                    locale={dateFnsLocale ?? undefined} // fallback to undefined if not loaded yet
                    onSelect={handleSelect}
                    startMonth={new Date(minDate.getFullYear(), minDate.getMonth())}
                    endMonth={toYear ? new Date(toYear, 11) : undefined}
                    disabled={[{ before: minDate }]}
                    onDayMouseEnter={handleDayMouseEnter}
                    onDayMouseLeave={handleDayMouseLeave}
                    components={{ ...customComponents, ...components }}
                    modifiers={modifiers}
                    modifiersClassNames={{
                        hoveredRange: 'hovered-range',
                        hoveredRangeTo: 'hovered-range-to',
                    }}
                    {...other}
                />
            </div>
        );
    });
